/* =============================================================
   AuthCallbackPage — Entra/MSAL redirect landing.

   The heavy lifting (state validation, token exchange, account
   storage) happens inside msalInstance.handleRedirectPromise(),
   which lib/msal.ts calls during module init. By the time we
   render here, MSAL has usually already finished and surfaced
   the new account via its event callback (lib/auth.tsx). This
   page just waits for the user to appear and then navigates.

   With navigateToLoginRequestUrl (lib/msal.ts), MSAL usually moves
   straight on to the page that started the sign-in (/account?next=…),
   which returns the person to `next` itself; this page covers the rest.
   ============================================================ */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { msalInstance, msalReady } from '../lib/msal';
import { goTo, safeNext } from '../lib/nav';

interface Status {
  kind: 'pending' | 'error';
  message?: string;
}


export function AuthCallbackPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [status, setStatus] = useState<Status>({ kind: 'pending' });
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const result = await msalReady;
        if (cancelled) return;

        /* If MSAL just consumed a redirect, its result has a `state`
           we use to pick the post-login destination. Otherwise check
           if MSAL already had an account before this mount. */
        const stateNext =
          result && 'state' in (result as object) ? (result as { state?: string }).state : null;
        const target = safeNext(stateNext, '/account');
        setTarget(target);

        if (msalInstance.getAllAccounts().length > 0) {
          goTo(navigate, target);
          return;
        }
        /* No account yet — give the auth event listener a beat
           (it's racing this effect), then re-check. */
        const tid = setTimeout(() => {
          if (cancelled) return;
          if (msalInstance.getAllAccounts().length > 0) {
            goTo(navigate, target);
          } else {
            setStatus({
              kind: 'error',
              message:
                "Sign-in didn't complete. The link may have expired or already been used. Try signing in again.",
            });
          }
        }, 800);
        return () => clearTimeout(tid);
      } catch (e: unknown) {
        if (cancelled) return;
        setStatus({
          kind: 'error',
          message: e instanceof Error ? e.message : String(e),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  /* Once useAuth knows about the user, leave immediately, to wherever sign-in started. */
  useEffect(() => {
    if (auth.user && target) goTo(navigate, target);
  }, [auth.user, target, navigate]);

  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <div className="eyebrow mb-4">Signing you in</div>
      {status.kind === 'pending' ? (
        <p className="font-editorial italic text-[17px] text-ink-soft">One moment…</p>
      ) : (
        <>
          <p className="font-editorial italic text-[17px] text-ink-soft leading-relaxed">
            {status.message}
          </p>
          <div className="mt-8 flex gap-7 justify-center">
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="btn-text"
            >
              Back to sign-in
            </button>
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="btn-text"
            >
              Create a new account
            </button>
          </div>
        </>
      )}
    </div>
  );
}
